import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Share2, Crown, BadgeCheck, Clock } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";

export default function Referrals() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/billing/referrals/my").then((r) => setCodes(r.data)).finally(() => setLoading(false));
  }, []);

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied");
  };

  const shareUrl = (code) => `${window.location.origin}/pricing?ref=${encodeURIComponent(code)}`;

  const copyLink = (code) => {
    navigator.clipboard.writeText(shareUrl(code));
    toast.success("Share link copied");
  };

  return (
    <div className="space-y-12" data-testid="referrals-root">
      <header>
        <p className="label-eyebrow">Referrals</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">Your referral codes</h1>
        <p className="text-[#888] mt-4 max-w-xl">
          Founding members receive one personal referral code. Share it with one person — they get 30% off Pro or Syndicate, you get the kind of word-of-mouth that builds a board.
        </p>
      </header>

      {loading ? (
        <div className="surface rounded-md p-8 text-sm text-[#666]">Loading…</div>
      ) : codes.length === 0 ? (
        <div className="surface rounded-md p-16 text-center" data-testid="referrals-empty">
          <Crown size={20} className="mx-auto text-[#c89f45] mb-3" />
          <p className="font-serif text-2xl">No referral codes yet.</p>
          <p className="text-sm text-[#888] mt-2">Codes are minted automatically when you become a Founding Member.</p>
        </div>
      ) : (
        <ul className="space-y-4">
          {codes.map((c) => {
            const used = (c.uses || 0) >= (c.max_uses || 1);
            return (
              <li key={c.id} className="surface rounded-md p-6 border-[#c89f45]/30" data-testid={`referral-${c.code}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <p className="label-eyebrow text-[#c89f45]">Personal code · {c.discount_percent}% off</p>
                      {used ? (
                        <span className="text-[10px] font-mono uppercase tracking-widest text-[#9bd3c4] bg-[#1B3B36] px-2 py-0.5 rounded">Redeemed</span>
                      ) : (
                        <span className="text-[10px] font-mono uppercase tracking-widest text-[#c89f45] bg-[#c89f45]/10 px-2 py-0.5 rounded">Available</span>
                      )}
                    </div>
                    <p className="font-mono text-4xl mt-3 tracking-tighter text-[#fafafa] select-all" data-testid={`code-${c.code}`}>{c.code}</p>
                    <p className="text-xs text-[#666] mt-3 font-mono">
                      APPLIES TO {(c.applies_to_plan_ids || []).join(" · ").toUpperCase()}
                    </p>
                    {(c.redemptions || []).length > 0 && (
                      <div className="mt-4 text-xs text-[#888]" data-testid={`redemption-${c.code}`}>
                        <span className="text-[#c89f45]">Redeemed</span> · {new Date(c.redemptions[0].at).toLocaleString()} · {c.redemptions[0].plan_id}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 md:w-56">
                    <Button
                      onClick={() => copy(c.code)}
                      className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] rounded-md"
                      data-testid={`copy-code-${c.code}`}
                    >
                      <Copy size={14} className="mr-2" /> Copy code
                    </Button>
                    <Button
                      onClick={() => copyLink(c.code)}
                      variant="outline"
                      className="bg-transparent border-[#2a2a2a] text-[#fafafa] hover:bg-[#1a1a1a] hover:text-white"
                      data-testid={`copy-link-${c.code}`}
                    >
                      <Share2 size={14} className="mr-2" /> Copy share link
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
